import * as React from 'react';
import { useTranslation } from '@shared/core/i18n';
import type { StylingProps } from './PeopleListItem.styles';
import {
  StyledItemInfo,
  AvatarWrapper,
  AvatarBorder,
  ItemText,
  ItemSubtext,
  TextContainer,
  DeliveryIconsRow,
  DeliveryIcon,
  UnreachableRowIndicator,
} from './PeopleListItem.styles';
import { Avatar, AvatarImage, AvatarFallback, Flex } from '@withjoy/joykit';
import { Phone, Envelope, WarningTriangle } from '@withjoy/joykit/icons';

export type InGroupPosition = 'first' | 'middle' | 'last' | '';

type SimpleProps = Readonly<{
  avatarUrl?: string;
  labelText: string;
  sublabelText?: string;
  disabled?: boolean;
  inGroupPosition?: InGroupPosition;
  isSmall?: boolean;
  showSublabelWhenSmall?: boolean;
  avatarName?: string;
  rightContent?: React.ReactNode;
}>;

const Simple: React.FC<SimpleProps> = React.memo(({ avatarUrl, labelText, sublabelText, disabled, isSmall, showSublabelWhenSmall, inGroupPosition, avatarName, rightContent }) => {
  const isNoNamePlusOne = inGroupPosition !== 'first' && !avatarName;
  const fallbackContent = avatarName ? undefined : inGroupPosition !== 'first' ? '+1' : '?';
  const subProps: StylingProps = { isSmall, inGroupPosition, disabled, isNoNamePlusOne };
  return (
    <StyledItemInfo {...subProps}>
      <AvatarWrapper>
        <Avatar
          size={isSmall ? 'sm' : 'lg'}
          name={avatarName || undefined}
        >
          {avatarUrl ? (
            <AvatarImage
              src={avatarUrl}
              alt={labelText + ' photo'}
            />
          ) : null}
          <AvatarFallback>{fallbackContent}</AvatarFallback>
        </Avatar>
        {inGroupPosition && <AvatarBorder />}
      </AvatarWrapper>
      <Flex
        flexDirection="column"
        overflow="hidden"
        flex={1}
      >
        <TextContainer>
          <ItemText>{labelText}</ItemText>
          {(!isSmall || showSublabelWhenSmall) && sublabelText && <ItemSubtext>{sublabelText}</ItemSubtext>}
        </TextContainer>
      </Flex>
      {rightContent}
    </StyledItemInfo>
  );
});

export type WithDeliveryMethodProps = SimpleProps &
  Readonly<{
    id: string;
    selected: boolean;
    enableSmsSelection: boolean;
    email?: string;
    hasPhoneNumber?: boolean;
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
    shouldHideEmailIcon?: boolean;
    shouldHidePhoneIcon?: boolean;
  }>;

const WithDeliveryMethod: React.FC<WithDeliveryMethodProps> = React.memo(
  ({
    id,
    selected,
    email,
    enableSmsSelection,
    hasPhoneNumber,
    hasEmailDeliveryMethodSelected,
    hasTextDeliveryMethodSelected,
    shouldHideEmailIcon,
    shouldHidePhoneIcon,
    labelText,
    ...baseProps
  }) => {
    const { tq } = useTranslation('peoplePicker');
    const isUnreachable = selected && !hasEmailDeliveryMethodSelected && !hasTextDeliveryMethodSelected;

    const deliveryIcons =
      enableSmsSelection && selected ? (
        <DeliveryIconsRow>
          {!shouldHidePhoneIcon && hasPhoneNumber && (
            <DeliveryIcon
              isSending={!!hasTextDeliveryMethodSelected}
              aria-label={hasTextDeliveryMethodSelected ? tq`Text: sending` : tq`Text: available`}
            >
              <Phone size={16} />
            </DeliveryIcon>
          )}
          {!shouldHideEmailIcon && email && (
            <DeliveryIcon
              isSending={!!hasEmailDeliveryMethodSelected}
              aria-label={hasEmailDeliveryMethodSelected ? tq`Email: sending` : tq`Email: available`}
            >
              <Envelope size={16} />
            </DeliveryIcon>
          )}
          {isUnreachable && (
            <UnreachableRowIndicator>
              <WarningTriangle size={14} />
            </UnreachableRowIndicator>
          )}
        </DeliveryIconsRow>
      ) : undefined;

    return (
      <Simple
        labelText={labelText}
        {...baseProps}
        rightContent={deliveryIcons}
      />
    );
  },
);

export const PeopleListItem = {
  Simple,
  WithDeliveryMethod,
};
